import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Share2, 
  Calendar, 
  Music, 
  MapPin, 
  Sparkles, 
  Users,
  Check,
  Menu,
  X,
  Instagram,
  Facebook,
  Mail,
  Phone,
  ChevronRight
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Design assets from guidelines
const ASSETS = {
  hero: "https://static.prod-images.emergentagent.com/jobs/6c9bda4d-9d0b-4ed5-b4eb-7365e062cfd0/images/a80f107705c8fe76f6bcf1f0772ba5efb71c740ef5ea8383ad9689fa73179d2b.png",
  background: "https://static.prod-images.emergentagent.com/jobs/6c9bda4d-9d0b-4ed5-b4eb-7365e062cfd0/images/553e8ec3ea32b2cd26d64c0d77dca95840fa9c2de81a61e716db523f517be41e.png",
  gallery: "https://static.prod-images.emergentagent.com/jobs/6c9bda4d-9d0b-4ed5-b4eb-7365e062cfd0/images/bfe5fef7d85abd5a75da3afa0780cd2bbce6f41ae2d748d642331ff01258a2f1.png",
  events: {
    mariage: "https://images.unsplash.com/photo-1599757628564-db10729b97f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwd2VkZGluZyUyMG5ldXRyYWwlMjBjaGFtcGFnbmV8ZW58MHx8fHwxNzc1NDY5MzQ0fDA&ixlib=rb-4.1.0&q=85",
    anniversaire: "https://images.unsplash.com/photo-1719936319623-f6abc9df620c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwd2VkZGluZyUyMG5ldXRyYWwlMjBjaGFtcGFnbmV8ZW58MHx8fHwxNzc1NDY5MzQ0fDA&ixlib=rb-4.1.0&q=85",
    bapteme: "https://images.unsplash.com/photo-1672094732506-e36eb9ba5de9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZmxvcmFsJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzU0NjkzNDR8MA&ixlib=rb-4.1.0&q=85",
    babyshower: "https://images.unsplash.com/photo-1464798429116-8e26f96b2e60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwZmxvcmFsJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzU0NjkzNDR8MA&ixlib=rb-4.1.0&q=85"
  }
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#pourquoi", label: "Pourquoi nous" },
    { href: "#evenements", label: "Événements" },
    { href: "#galerie", label: "Galerie" },
    { href: "#nos-offres", label: "Nos offres" },
    { href: "#contact", label: "Contact" }
  ];

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass border-b border-white/20 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container-custom">
        <nav className="flex items-center justify-between h-20">
          <a 
            href="/" 
            className="font-serif text-2xl font-semibold text-stone-900"
            data-testid="logo"
          >
            Mon Invitation Magique
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm text-stone-600 hover:text-[#C5A028] transition-colors tracking-wide"
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="hidden lg:block">
            <a 
              href="#contact"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="cta-header"
            >
              Demander un devis
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:hidden py-4 border-t border-stone-200"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 text-stone-600 hover:text-[#C5A028]"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a 
              href="#contact"
              className="btn-primary mt-4 inline-block text-center w-full"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Demander un devis
            </a>
          </motion.div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section 
      data-testid="hero-section"
      className="min-h-screen flex items-center pt-20 bg-[#FDFBF7]"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#C5A028] mb-6 block">
              Invitations digitales sur mesure
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight text-stone-900 leading-[1.1] mb-6">
              Une invitation unique pour un moment inoubliable
            </h1>
            <p className="text-lg text-stone-600 leading-relaxed mb-8 max-w-lg">
              Des invitations digitales élégantes et personnalisées pour annoncer vos plus beaux événements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="#contact" 
                className="btn-primary text-center"
                data-testid="cta-create"
              >
                Créer mon invitation
              </a>
              <a 
                href="#galerie" 
                className="btn-secondary text-center"
                data-testid="cta-gallery"
              >
                Voir les modèles
              </a>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <div className="relative">
              <motion.img
                src={ASSETS.hero}
                alt="Invitation digitale élégante sur smartphone"
                className="w-full max-w-md mx-auto lg:max-w-none rounded-2xl shadow-2xl"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
              <div className="absolute -z-10 inset-0 bg-gradient-to-br from-[#EEDEA8]/30 to-transparent rounded-3xl transform translate-x-4 translate-y-4"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    { icon: Sparkles, title: "Invitation élégante et moderne", description: "Des designs raffinés qui impressionnent vos invités dès le premier regard." },
    { icon: Share2, title: "Lien facile à partager", description: "Un simple lien à envoyer par message, email ou réseaux sociaux." },
    { icon: Users, title: "RSVP automatique", description: "Gérez facilement les réponses de vos invités en temps réel." },
    { icon: Music, title: "Musique et animations", description: "Une expérience immersive avec une bande sonore personnalisée." },
    { icon: MapPin, title: "Google Maps intégré", description: "Vos invités trouvent facilement le lieu de votre événement." },
    { icon: Calendar, title: "Expérience immersive", description: "Un moment magique qui met en valeur votre événement." }
  ];

  return (
    <section 
      id="pourquoi" 
      data-testid="benefits-section"
      className="section-spacing bg-white"
    >
      <div className="container-custom">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span 
            variants={fadeInUp}
            className="text-sm uppercase tracking-[0.2em] font-medium text-[#C5A028] mb-4 block"
          >
            Nos avantages
          </motion.span>
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-serif text-stone-900"
          >
            Pourquoi choisir une invitation digitale ?
          </motion.h2>
        </motion.div>

        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="group"
            >
              <div className="bg-[#FDFBF7] text-[#C5A028] p-4 rounded-full w-fit mb-6 group-hover:bg-[#C5A028] group-hover:text-white transition-colors duration-300">
                <benefit.icon size={24} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif text-stone-900 mb-3">{benefit.title}</h3>
              <p className="text-stone-600 leading-relaxed">{benefit.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Events Section
const EventsSection = () => {
  const events = [
    { name: "Mariage", image: ASSETS.events.mariage },
    { name: "Anniversaire", image: ASSETS.events.anniversaire },
    { name: "Baby Shower", image: ASSETS.events.babyshower },
    { name: "Baptême", image: ASSETS.events.bapteme }
  ];

  return (
    <section 
      id="evenements" 
      data-testid="events-section"
      className="section-spacing bg-[#FDFBF7]"
    >
      <div className="container-custom">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span 
            variants={fadeInUp}
            className="text-sm uppercase tracking-[0.2em] font-medium text-[#C5A028] mb-4 block"
          >
            Pour chaque occasion
          </motion.span>
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-serif text-stone-900"
          >
            Nos événements
          </motion.h2>
        </motion.div>

        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {events.map((event, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="group relative overflow-hidden rounded-2xl aspect-[3/4] cursor-pointer img-zoom"
            >
              <img
                src={event.image}
                alt={event.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-serif text-white">{event.name}</h3>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const galleryItems = [
    {
      id: 1,
      title: "Champagne",
      subtitle: "Royal et sophistiqué",
      image: ASSETS.hero,
      style: "lg:col-span-2 lg:row-span-2"
    },
    {
      id: 2,
      title: "Le Voile",
      subtitle: "Voile ivoire élégant",
      image: "https://images.unsplash.com/photo-1612619732485-1ae018b63c55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwd2VkZGluZyUyMGludml0YXRpb24lMjBnb2xkJTIwYmVpZ2UlMjBtaW5pbWFsfGVufDB8fHx8MTc3NTQ3MDgzMnww&ixlib=rb-4.1.0&q=85",
      style: ""
    },
    {
      id: 3,
      title: "Minimaliste",
      subtitle: "Épuré et moderne",
      image: "https://images.unsplash.com/photo-1742581659446-6260fc707e7d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxlbGVnYW50JTIwd2VkZGluZyUyMGludml0YXRpb24lMjBnb2xkJTIwYmVpZ2UlMjBtaW5pbWFsfGVufDB8fHx8MTc3NTQ3MDgzMnww&ixlib=rb-4.1.0&q=85",
      style: ""
    },
    {
      id: 4,
      title: "Floral",
      subtitle: "Doux et romantique",
      image: "https://images.unsplash.com/photo-1612619731486-ab0ce3288d6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwd2VkZGluZyUyMGludml0YXRpb24lMjBnb2xkJTIwYmVpZ2UlMjBtaW5pbWFsfGVufDB8fHx8MTc3NTQ3MDgzMnww&ixlib=rb-4.1.0&q=85",
      style: ""
    },
    {
      id: 5,
      title: "La Porte",
      subtitle: "Lumière impériale",
      image: ASSETS.gallery,
      style: "lg:col-span-2"
    }
  ];

  return (
    <section 
      id="galerie" 
      data-testid="gallery-section"
      className="section-spacing bg-white"
    >
      <div className="container-custom">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span 
            variants={fadeInUp}
            className="text-sm uppercase tracking-[0.2em] font-medium text-[#C5A028] mb-4 block"
          >
            Nos univers
          </motion.span>
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-serif text-stone-900 mb-4"
          >
            Choisissez votre style, rendez-le unique
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-stone-600 max-w-2xl mx-auto"
          >
            Chaque univers est pensé pour raconter votre histoire. Découvrez nos créations et laissez-vous inspirer.
          </motion.p>
        </motion.div>

        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6"
        >
          {galleryItems.map((item) => (
            <motion.div
              key={item.id}
              variants={fadeInUp}
              className={`group relative overflow-hidden rounded-2xl cursor-pointer ${item.style}`}
            >
              <div className="aspect-[3/4] lg:aspect-auto lg:h-full min-h-[200px] lg:min-h-[300px]">
                <img
                  src={item.image}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                  <h3 className="text-lg lg:text-xl font-serif text-white">{item.title}</h3>
                  <p className="text-white/70 text-sm mt-1">{item.subtitle}</p>
                </div>
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="bg-white/20 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full">
                    Voir la démo
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Features inspired by Revelia */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 grid md:grid-cols-3 lg:grid-cols-6 gap-6 text-center"
        >
          {[
            { icon: Sparkles, title: "Design sur-mesure", desc: "Une création unique" },
            { icon: "door", title: "Animation d'ouverture", desc: "Effet enveloppe élégant" },
            { icon: Music, title: "Musique personnalisée", desc: "Votre ambiance" },
            { icon: Users, title: "Gestion RSVP", desc: "Suivi des confirmations" },
            { icon: Check, title: "Modifications incluses", desc: "Jusqu'à validation" },
            { icon: Calendar, title: "Livraison 48-72h", desc: "Rapidité garantie" }
          ].map((feature, index) => (
            <div key={index} className="p-4">
              <div className="w-12 h-12 bg-[#FDFBF7] rounded-full flex items-center justify-center mx-auto mb-3">
                {feature.icon === "door" ? (
                  <span className="text-[#C5A028] text-xl">🚪</span>
                ) : (
                  <feature.icon className="text-[#C5A028]" size={20} />
                )}
              </div>
              <h4 className="font-medium text-stone-900 text-sm">{feature.title}</h4>
              <p className="text-stone-500 text-xs mt-1">{feature.desc}</p>
            </div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a 
            href="/personnalisation" 
            className="btn-primary inline-flex items-center gap-2"
          >
            Créer mon invitation
            <ChevronRight size={16} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const packages = [
    {
      id: "essentiel",
      name: "Pack Essentiel",
      price: "79",
      features: [
        "Invitation digitale personnalisée",
        "Lien partageable",
        "Design élégant",
        "Support par email"
      ],
      highlighted: false
    },
    {
      id: "animation",
      name: "Pack Animation",
      price: "119",
      features: [
        "Invitation animée",
        "Musique personnalisée",
        "Galerie photos",
        "Animations élégantes",
        "Support prioritaire"
      ],
      highlighted: true
    },
    {
      id: "premium",
      name: "Pack Premium",
      price: "179",
      features: [
        "Invitation interactive",
        "RSVP invités intégré",
        "Google Maps intégré",
        "Animations premium",
        "Musique personnalisée",
        "Support dédié"
      ],
      highlighted: false
    }
  ];

  return (
    <section 
      id="nos-offres" 
      data-testid="pricing-section"
      className="section-spacing bg-[#FDFBF7] relative"
      style={{
        backgroundImage: `url(${ASSETS.background})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      <div className="absolute inset-0 bg-[#FDFBF7]/90"></div>
      <div className="container-custom relative z-10">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span 
            variants={fadeInUp}
            className="text-sm uppercase tracking-[0.2em] font-medium text-[#C5A028] mb-4 block"
          >
            Tarifs transparents
          </motion.span>
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-serif text-stone-900"
          >
            Nos offres
          </motion.h2>
        </motion.div>

        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto"
        >
          {packages.map((pkg) => (
            <motion.div
              key={pkg.id}
              variants={fadeInUp}
              className={`bg-white rounded-2xl p-8 shadow-lg card-hover relative ${
                pkg.highlighted ? "pricing-highlight" : "border border-stone-100"
              }`}
              data-testid={`pricing-card-${pkg.id}`}
            >
              <h3 className="text-xl font-serif text-stone-900 mb-2">{pkg.name}</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl font-serif text-stone-900">{pkg.price}</span>
                <span className="text-stone-500">€</span>
              </div>
              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-start gap-3 text-stone-600 text-sm">
                    <Check className="text-[#C5A028] flex-shrink-0 mt-0.5" size={16} />
                    {feature}
                  </li>
                ))}
              </ul>
              <a
                href={`/personnalisation?pack=${pkg.id}`}
                className={`w-full flex items-center justify-center ${
                  pkg.highlighted 
                    ? "bg-[#C5A028] hover:bg-[#b89422] text-white" 
                    : "bg-stone-900 hover:bg-stone-800 text-white"
                } rounded-full py-3 transition-colors`}
                data-testid={`cta-btn-${pkg.id}`}
              >
                Demander un devis
              </a>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-stone-500 text-sm mt-8"
        >
          Tous nos packs sont personnalisables selon vos besoins
        </motion.p>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const steps = [
    { number: "01", title: "Choisissez votre style", description: "Parcourez notre galerie et sélectionnez le design qui correspond à votre événement." },
    { number: "02", title: "Envoyez vos informations", description: "Transmettez-nous les détails de votre événement : date, lieu, message personnalisé." },
    { number: "03", title: "Recevez votre invitation", description: "Votre invitation digitale est prête à être partagée avec vos invités." }
  ];

  return (
    <section 
      data-testid="how-it-works-section"
      className="section-spacing bg-white"
    >
      <div className="container-custom">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span 
            variants={fadeInUp}
            className="text-sm uppercase tracking-[0.2em] font-medium text-[#C5A028] mb-4 block"
          >
            Simple et rapide
          </motion.span>
          <motion.h2 
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-serif text-stone-900"
          >
            Comment ça fonctionne ?
          </motion.h2>
        </motion.div>

        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto"
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="relative text-center px-4"
            >
              <span className="font-serif text-7xl font-light text-[#C5A028]/15 absolute -top-4 left-1/2 -translate-x-1/2">
                {step.number}
              </span>
              <div className="relative pt-12">
                <h3 className="text-xl font-serif text-stone-900 mb-3">{step.title}</h3>
                <p className="text-stone-600 leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    type_evenement: "",
    date_evenement: null,
    lieu: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          date_evenement: formData.date_evenement 
            ? format(formData.date_evenement, "dd/MM/yyyy") 
            : null
        })
      });

      if (!response.ok) throw new Error("Erreur lors de l'envoi");

      toast.success("Merci ! Votre demande a bien été envoyée.");
      setFormData({
        nom: "",
        email: "",
        type_evenement: "",
        date_evenement: null,
        lieu: "",
        message: ""
      });
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      data-testid="contact-section"
      className="section-spacing bg-[#F4F0EA] relative"
      style={{
        backgroundImage: `url(${ASSETS.background})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      <div className="absolute inset-0 bg-[#F4F0EA]/95"></div>
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-sm uppercase tracking-[0.2em] font-medium text-[#C5A028] mb-4 block">
              Contactez-nous
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-stone-900 mb-6">
              Parlez-nous de votre événement
            </h2>
            <p className="text-stone-600 leading-relaxed mb-8">
              Nous sommes là pour créer l'invitation parfaite pour votre moment spécial. Remplissez le formulaire et nous vous répondrons dans les plus brefs délais.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-white p-3 rounded-full">
                  <Mail className="text-[#C5A028]" size={20} />
                </div>
                <div>
                  <p className="font-medium text-stone-900">Email</p>
                  <a href="mailto:contact@moninvitationmagique.fr" className="text-stone-600 hover:text-[#C5A028]">
                    contact@moninvitationmagique.fr
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="bg-white p-3 rounded-full">
                  <MapPin className="text-[#C5A028]" size={20} />
                </div>
                <div>
                  <p className="font-medium text-stone-900">Adresse</p>
                  <p className="text-stone-600">
                    5 impasse Jean Jammes<br />
                    66300 Saint-Jean-Lasseille<br />
                    France
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Nom *</label>
                  <Input
                    type="text"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    required
                    className="border-stone-200 focus:border-[#C5A028] focus:ring-[#C5A028]"
                    placeholder="Votre nom"
                    data-testid="input-nom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="border-stone-200 focus:border-[#C5A028] focus:ring-[#C5A028]"
                    placeholder="votre@email.com"
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Type d'événement *</label>
                  <Select
                    value={formData.type_evenement}
                    onValueChange={(value) => setFormData({ ...formData, type_evenement: value })}
                    required
                  >
                    <SelectTrigger className="border-stone-200 focus:border-[#C5A028] focus:ring-[#C5A028]" data-testid="select-event-type">
                      <SelectValue placeholder="Sélectionnez" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mariage">Mariage</SelectItem>
                      <SelectItem value="anniversaire">Anniversaire</SelectItem>
                      <SelectItem value="baby-shower">Baby Shower</SelectItem>
                      <SelectItem value="bapteme">Baptême</SelectItem>
                      <SelectItem value="autre">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Date de l'événement</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal border-stone-200"
                        data-testid="date-picker-trigger"
                      >
                        <Calendar className="mr-2 h-4 w-4 text-stone-500" />
                        {formData.date_evenement 
                          ? format(formData.date_evenement, "dd MMMM yyyy", { locale: fr })
                          : <span className="text-stone-500">Sélectionnez une date</span>
                        }
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.date_evenement}
                        onSelect={(date) => setFormData({ ...formData, date_evenement: date })}
                        locale={fr}
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-stone-700 mb-2">Lieu</label>
                <Input
                  type="text"
                  value={formData.lieu}
                  onChange={(e) => setFormData({ ...formData, lieu: e.target.value })}
                  className="border-stone-200 focus:border-[#C5A028] focus:ring-[#C5A028]"
                  placeholder="Ville ou adresse de l'événement"
                  data-testid="input-lieu"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-stone-700 mb-2">Message *</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={4}
                  className="border-stone-200 focus:border-[#C5A028] focus:ring-[#C5A028]"
                  placeholder="Décrivez votre projet d'invitation..."
                  data-testid="input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#0A0A0A] hover:bg-stone-800 text-white rounded-full py-6"
                data-testid="submit-form"
              >
                {isSubmitting ? "Envoi en cours..." : "Recevoir mon invitation"}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer 
      data-testid="footer"
      className="bg-stone-900 text-stone-300 py-16"
    >
      <div className="container-custom">
        <div className="grid md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <h3 className="font-serif text-2xl text-white mb-4">Mon Invitation Magique</h3>
            <p className="text-stone-400 leading-relaxed mb-6">
              Des invitations digitales élégantes pour vos moments les plus précieux.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-stone-800 p-3 rounded-full hover:bg-[#C5A028] transition-colors"
                data-testid="social-instagram"
              >
                <Instagram size={18} />
              </a>
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-stone-800 p-3 rounded-full hover:bg-[#C5A028] transition-colors"
                data-testid="social-facebook"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-medium text-white mb-4">Navigation</h4>
            <ul className="space-y-3">
              <li><a href="#pourquoi" className="hover:text-[#C5A028] transition-colors">Pourquoi nous</a></li>
              <li><a href="#evenements" className="hover:text-[#C5A028] transition-colors">Événements</a></li>
              <li><a href="#galerie" className="hover:text-[#C5A028] transition-colors">Galerie</a></li>
              <li><a href="#nos-offres" className="hover:text-[#C5A028] transition-colors">Nos offres</a></li>
              <li><a href="#contact" className="hover:text-[#C5A028] transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-medium text-white mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-[#C5A028]" />
                <a href="mailto:contact@moninvitationmagique.fr" className="hover:text-[#C5A028] transition-colors">
                  contact@moninvitationmagique.fr
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} className="text-[#C5A028] mt-1" />
                <span>
                  5 impasse Jean Jammes<br />
                  66300 Saint-Jean-Lasseille<br />
                  France
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-stone-800 mt-12 pt-8 text-center text-stone-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Mon Invitation Magique. Tous droits réservés.</p>
          <p className="mt-2">
            <a href="https://www.moninvitationmagique.fr" className="hover:text-[#C5A028] transition-colors">
              www.moninvitationmagique.fr
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main HomePage Component
const HomePage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <BenefitsSection />
        <EventsSection />
        <GallerySection />
        <PricingSection />
        <HowItWorksSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
